import { useRouteError } from '@remix-run/react';
import { NavBar } from '~/components/nav-bar';
import {
  TeaScript,
  SlardarScript,
  TeaDeferredScript,
  SlardarDeferredScript,
  FacebookDeferredScript,
  WebVitalMonitor,
  PumbaaScript,
  // USBanBanner,
  // USBanModal,
} from '@lemon8/web-app-shared/component';
import { BodyOutlet, JotaiStoreInjector, HeadOutlet, I18nProvider, Scripts, Links } from '@lemon8/web-app-shared/ssr';
import { NavDrawer } from '~/components/nav-drawer';
import { BottomReflow } from '~/components/bottom-reflow';
import { SharePanel } from '~/components/share-panel';
import { AutoScrollToTop, PageShowReporter, Preconnect } from './impl';
import CookieBanner from '../../components/cookie-banner';
import { LandingModalTest } from '~/components/landing-modal-test';
import { LayerManager } from '@discover/shared/layer-manager';
import { Helmet } from './impl/helmet';
import { NoSSR } from '~/utils/no-ssr';
import { SimpleCtaModal } from '~/components/simple-cta-modal';
import { FC, PropsWithChildren, Suspense, useEffect, useMemo } from 'react';
import { PageTypeUtils } from 'shared/models/page-type';
import { RootSkeleton } from '~/skeleton';
import { FeedCategoryBar } from '~/components/feed-category-bar';
import { WebSiteJSONLD } from '~/utils/json-ld/website';
import { blankScreenPlugin } from '@slardar/integrations/dist/blankscreen';
import { PCReflow } from '~/components/pc-reflow';
import { PinterestBody, PinterestHead } from '~/routes/#root/impl/pinterest';
import { createStore, Provider } from 'jotai';
import { Atoms, HydrationAtomFamiliesMap, HydrationAtomsMap } from '~/models';
import { NotFound } from '~/components/not-found';
import { LiveReload } from '@ttark/components';

import './root.scss';

const RootHtml: FC = () => {
  return (
    <html>
      <head>
        <Helmet />
        <Preconnect />
        <TeaScript />
        <SlardarScript
          pageview={{
            extractPid: (url: string) => {
              const u = new URL(url);
              const pageType = PageTypeUtils.getPageTypeFromPathname(u.pathname);
              if (pageType) {
                return `seo_${pageType}`;
              }
              return 'seo_unknown';
            },
          }}
          integrations={[blankScreenPlugin({ rootSelector: 'main' })]}
        />
        <PinterestHead />
        <WebSiteJSONLD />
        <HeadOutlet />
        <Links />
        <PumbaaScript />
        <TeaDeferredScript />
        <SlardarDeferredScript />
        <FacebookDeferredScript />
      </head>
      <body>
        <NavBar />
        <main>
          <FeedCategoryBar />
          <Suspense fallback={<RootSkeleton />}>
            <BodyOutlet />
          </Suspense>
        </main>
        <aside>
          <CookieBanner />
          <SharePanel />
          <LandingModalTest />
          {/* <USBanModal />
          <USBanBanner /> */}
          <NavDrawer />
          <NoSSR>
            <SimpleCtaModal />
            <PCReflow />
          </NoSSR>
        </aside>
        <footer>
          <BottomReflow />
        </footer>
        <Scripts />
        <LayerManager />
        <LiveReload />
        <PinterestBody />
      </body>
    </html>
  );
};

export const RootLayout: FC<PropsWithChildren> = ({ children }) => {
  if (__BUILD_TYPE__ !== 'online') {
    console.log('root layout re-rendering');
  }

  const store = useMemo(() => {
    if (__BUILD_TARGET__ === 'client') {
      return window.store;
    } else {
      return createStore();
    }
  }, []);

  useEffect(() => {
    (window as any).atoms = Atoms;
    window.history.scrollRestoration = 'manual';
  }, []);

  return (
    <Provider store={store}>
      <JotaiStoreInjector atoms={HydrationAtomsMap} atomFamilies={HydrationAtomFamiliesMap} />
      <PageShowReporter />
      <WebVitalMonitor />
      <AutoScrollToTop />
      <I18nProvider>{children}</I18nProvider>
    </Provider>
  );
};

export const Root: FC = () => <RootHtml />;

export const RootErrorBoundary: FC = () => {
  const error = useRouteError();

  if (__BUILD_TARGET__ === 'client' && error instanceof Error) {
    window.Slardar('captureException', error);
  }

  return (
    <html>
      <head>
        <Helmet />
        <title>Lemon8 Oops...</title>
        <Links />
      </head>
      <body>
        <NotFound />
        <Scripts />
      </body>
    </html>
  );
};
